import { z } from 'zod';

import {
  CreatedMetadataSchema,
  EntityBaseSchema,
  NonEmptyStringSchema,
  UpdatedMetadataSchemaOptional,
} from '../../base';

// PRIMITIVE SCHEMAS
export const IntegrationProviderSchema = NonEmptyStringSchema.describe(
  'Integration provider (e.g., "jira", "notion")',
);

export const ParagonConnectionIdSchema = NonEmptyStringSchema.describe(
  'Paragon connection ID',
);

export const ParagonUserIdSchema = NonEmptyStringSchema.describe(
  'Signed user ID for verification',
);

export const IntegrationEnvironmentSchema = z
  .enum(['sandbox', 'preRelease', 'live'])
  .describe('Client environment for this integration connection');

export const ClientEnvsSchema = z
  .object({
    sandbox: z.boolean(),
    preRelease: z.boolean(),
    live: z.boolean(),
  })
  .describe('Which client environments this integration is enabled in');

export const MCPFunctionNameSchema =
  NonEmptyStringSchema.describe('MCP function name');

export const MCPTimeoutSchema = z
  .number()
  .int()
  .positive()
  .describe('MCP timeout in milliseconds');

export const AvailableFunctionsSchema = z
  .array(MCPFunctionNameSchema)
  .describe('List of enabled function names');

export const IntegrationConnectedAtSchema = z
  .string()
  .datetime()
  .describe('ISO 8601 connection timestamp');

// PROVIDER METADATA SCHEMAS
// Generic metadata structure that works for all providers
export const IntegrationMetadataSchema = z
  .object({
    integrationName: NonEmptyStringSchema,
    connectedAt: IntegrationConnectedAtSchema,
  })
  .catchall(z.union([z.string(), z.number(), z.boolean()]))
  .describe(
    'Generic integration metadata with required fields and optional provider-specific fields',
  );

// COMPOSITE SCHEMAS
export const MCPConfigSchema = z
  .object({
    enabledFunctions: AvailableFunctionsSchema,
    timeout: MCPTimeoutSchema,
  })
  .describe('MCP configuration');

// CLIENT CONNECTION CONFIG (with metadata for UI state management)
export const ClientConnectionSchema = EntityBaseSchema.extend({
  paragonConnectionId: ParagonConnectionIdSchema,
  paragonUserId: ParagonUserIdSchema,
  provider: IntegrationProviderSchema,
  metadata: IntegrationMetadataSchema,
})
  .merge(CreatedMetadataSchema)
  .merge(UpdatedMetadataSchemaOptional);

// CONNECTION ID REFERENCE SCHEMA - for referencing connections in environments
export const ConnectionIdReferenceSchema =
  NonEmptyStringSchema.optional().describe('Connection ID reference');

// CLIENT ENVIRONMENTS SCHEMA - contains connection ID references for each environment
export const ClientEnvironmentsSchema = z
  .object({
    sandbox: ConnectionIdReferenceSchema,
    preRelease: ConnectionIdReferenceSchema,
    live: ConnectionIdReferenceSchema,
  })
  .describe('Client environment connection ID references');

// CONNECTIONS MAP SCHEMA - pool of all connections for a provider
export const ConnectionsMapSchema = z
  .record(NonEmptyStringSchema, ClientConnectionSchema)
  .describe('Map of connection ID to connection object');

// INTEGRATION CONFIG - with metadata for UI state
export const IntegrationConfigSchema = z.object({
  provider: IntegrationProviderSchema,
  availableFunctions: AvailableFunctionsSchema,
  timeout: MCPTimeoutSchema.default(30000),
  clientEnvs: ClientEnvironmentsSchema,
  connections: ConnectionsMapSchema,
});

// TYPE EXPORTS
export type IntegrationProvider = z.infer<typeof IntegrationProviderSchema>;
export type IntegrationEnvironment = z.infer<
  typeof IntegrationEnvironmentSchema
>;
export type ClientEnvs = z.infer<typeof ClientEnvsSchema>;
export type ClientConnection = z.infer<typeof ClientConnectionSchema>;
export type ClientEnvironments = z.infer<typeof ClientEnvironmentsSchema>;
export type ConnectionsMap = z.infer<typeof ConnectionsMapSchema>;
export type AvailableFunctions = z.infer<typeof AvailableFunctionsSchema>;
export type MCPConfig = z.infer<typeof MCPConfigSchema>;
export type IntegrationMetadata = z.infer<typeof IntegrationMetadataSchema>;
export type IntegrationConfig = z.infer<typeof IntegrationConfigSchema>;
